import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Hammer, Eye, EyeOff, Mail, Lock, User, Building, AlertCircle, CheckCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../utils/formatters';
import { input, label } from '../../styles/components';

const registerSchema = z.object({
  name: z.string().min(2, 'Mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'Mínimo 8 caracteres'),
  confirmPassword: z.string(),
  companyName: z.string().min(2, 'Mínimo 2 caracteres'),
  acceptTerms: z.boolean().refine(v => v, 'Debes aceptar los términos'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const { register: registerUser, isLoading: authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterForm) => {
    setError('');
    const result = await registerUser({
      name: data.name,
      email: data.email,
      password: data.password,
      confirmPassword: data.confirmPassword,
      companyName: data.companyName,
      acceptTerms: data.acceptTerms,
    });
    if (result.success) {
      setRegisteredEmail(data.email);
      setSubmitted(true);
    } else {
      setError(result.error || 'Error al registrar');
    }
  };

  if (submitted) {
    return (
      <div className="b2b-panel p-8 text-center space-y-5">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-[color:var(--b2b-success)]/10">
          <CheckCircle className="w-8 h-8 text-[color:var(--b2b-success)]" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight mb-1">Solicitud enviada</h1>
          <p className="b2b-muted">
            Hemos recibido tu registro. Te hemos enviado un correo de confirmación a <strong>{registeredEmail}</strong>.
          </p>
        </div>

        <div className="p-4 text-left bg-[var(--b2b-surface-muted)] rounded-lg border border-[var(--b2b-border)]">
          <p className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Mail className="w-4 h-4" /> Pendiente de aprobación
          </p>
          <p className="text-sm b2b-muted">
            Tu cuenta será revisada por un administrador. Recibirás un correo cuando esté <strong>activada</strong> y ya podrás iniciar sesión.
          </p>
        </div>

        <Link to="/login" className="inline-block text-sm font-medium text-[color:var(--b2b-accent-strong)] hover:underline">
          Volver a iniciar sesión
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-[var(--b2b-accent)]">
            <Hammer className="w-7 h-7 text-white" />
          </span>
          <span className="text-2xl font-extrabold tracking-tight">ReformAll</span>
        </Link>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Crear cuenta</h1>
          <p className="b2b-muted">Regístrate y empieza a gestionar tus reformas</p>
        </div>
      </div>

      <div className="b2b-panel p-6 sm:p-8">
        {error && (
          <div className="mb-6 flex items-center gap-2 p-3 rounded-lg b2b-pill b2b-pill--danger">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className={label}>Nombre completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
                <input
                  {...register('name')}
                  id="name"
                  type="text"
                  autoComplete="name"
                  className={cn(input, 'pl-9')}
                  placeholder="Juan Pérez"
                  disabled={isSubmitting || authLoading}
                  aria-invalid={!!errors.name}
                />
              </div>
              {errors.name && <p className="mt-1.5 text-sm text-[color:var(--b2b-danger)]">{errors.name.message}</p>}
            </div>
            <div>
              <label htmlFor="companyName" className={label}>Nombre de la empresa</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
                <input
                  {...register('companyName')}
                  id="companyName"
                  type="text"
                  className={cn(input, 'pl-9')}
                  placeholder="Reformas Pérez SL"
                  disabled={isSubmitting || authLoading}
                  aria-invalid={!!errors.companyName}
                />
              </div>
              {errors.companyName && <p className="mt-1.5 text-sm text-[color:var(--b2b-danger)]">{errors.companyName.message}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="email" className={label}>Correo electrónico</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
              <input
                {...register('email')}
                id="email"
                type="email"
                autoComplete="email"
                className={cn(input, 'pl-9')}
                placeholder="tu@email.com"
                disabled={isSubmitting || authLoading}
                aria-invalid={!!errors.email}
              />
            </div>
            {errors.email && <p className="mt-1.5 text-sm text-[color:var(--b2b-danger)]">{errors.email.message}</p>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="password" className={label}>Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
                <input
                  {...register('password')}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={cn(input, 'pl-9 pr-10')}
                  placeholder="••••••••"
                  disabled={isSubmitting || authLoading}
                  aria-invalid={!!errors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 b2b-muted"
                  aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1.5 text-sm text-[color:var(--b2b-danger)]">{errors.password.message}</p>}
            </div>
            <div>
              <label htmlFor="confirmPassword" className={label}>Confirmar contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 b2b-muted" />
                <input
                  {...register('confirmPassword')}
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={cn(input, 'pl-9')}
                  placeholder="••••••••"
                  disabled={isSubmitting || authLoading}
                  aria-invalid={!!errors.confirmPassword}
                />
              </div>
              {errors.confirmPassword && <p className="mt-1.5 text-sm text-[color:var(--b2b-danger)]">{errors.confirmPassword.message}</p>}
            </div>
          </div>

          <div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                {...register('acceptTerms')}
                type="checkbox"
                className="mt-1 w-4 h-4 rounded accent-[color:var(--b2b-accent)]"
              />
              <span className="text-sm b2b-muted">
                Acepto los <Link to="/terms" className="font-medium text-[color:var(--b2b-accent-strong)] hover:underline">Términos y Condiciones</Link> y la{' '}
                <Link to="/privacy" className="font-medium text-[color:var(--b2b-accent-strong)] hover:underline">Política de Privacidad</Link>
              </span>
            </label>
            {errors.acceptTerms && <p className="mt-1.5 text-sm text-[color:var(--b2b-danger)]">{errors.acceptTerms.message}</p>}
          </div>

          <button
            type="submit"
            className="b2b-btn b2b-btn--primary w-full justify-center disabled:opacity-60"
            disabled={isSubmitting || authLoading}
          >
            {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm b2b-muted">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="font-medium text-[color:var(--b2b-accent-strong)] hover:underline">Iniciar sesión</Link>
        </p>
      </div>
    </div>
  );
}
